import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import {
  getCatalog,
  getSettings,
  getSuggestions,
  listCollections,
  listHouseholds,
  listCopies,
  updateSettings,
  type CopyListItem,
  type RouteBody,
  type Settings,
} from '@vinyl/shared';
import { api } from '../lib/api.ts';
import { indexCopies } from './browse.ts';

export const keys = {
  settings: ['settings'] as const,
  collections: ['collections'] as const,
  copies: (collectionId: string) => ['copies', collectionId] as const,
  copy: (id: string) => ['copy', id] as const,
  suggestions: ['suggestions'] as const,
  households: ['households'] as const,
  catalog: ['catalog'] as const,
};

/** The User's catalog mode (user-settings spec); decides whether Discogs is offered. */
export function useCatalog() {
  return useQuery({ queryKey: keys.catalog, queryFn: () => api(getCatalog), staleTime: 60_000 });
}

export function useSettings() {
  return useQuery({ queryKey: keys.settings, queryFn: () => api(getSettings), staleTime: 60_000 });
}

export function useUpdateSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: RouteBody<typeof updateSettings>) => api(updateSettings, { body }),
    // Show the choice at once; the server's answer replaces it.
    onMutate: (body) => {
      const previous = queryClient.getQueryData<Settings>(keys.settings);
      if (previous) queryClient.setQueryData(keys.settings, { ...previous, ...body });
      return { previous };
    },
    onError: (_error, _body, context) => {
      if (context?.previous) queryClient.setQueryData(keys.settings, context.previous);
    },
    onSuccess: (settings: Settings) => queryClient.setQueryData(keys.settings, settings),
  });
}

export function useCollections() {
  return useQuery({ queryKey: keys.collections, queryFn: () => api(listCollections), select: (r) => r.collections });
}

/** The Collection's Copies with a search index built once per list (docs/architecture.md, Performance). */
export function useCopies(collectionId: string | undefined) {
  const query = useQuery({
    queryKey: keys.copies(collectionId ?? 'none'),
    queryFn: () => api(listCopies, { params: { id: collectionId! } }),
    enabled: !!collectionId,
    select: (r) => r.copies,
  });
  const indexed = useMemo(() => (query.data ? indexCopies(query.data) : []), [query.data]);
  return { ...query, indexed };
}

/** The User's Households with their Copy counts (households spec). */
export function useHouseholds() {
  return useQuery({ queryKey: keys.households, queryFn: () => api(listHouseholds), select: (r) => r.households });
}

export function useSuggestions() {
  return useQuery({ queryKey: keys.suggestions, queryFn: () => api(getSuggestions), staleTime: 30_000 });
}

/**
 * Puts a saved Copy into the cached Collection list instead of downloading
 * the whole Collection again (collection-browsing spec, Edit in a large Collection).
 */
export function putCopyInCache(queryClient: QueryClient, copy: CopyListItem, previousCollectionId?: string) {
  if (previousCollectionId && previousCollectionId !== copy.collectionId) removeCopyFromCache(queryClient, previousCollectionId, copy.id);
  queryClient.setQueryData<{ copies: CopyListItem[] }>(keys.copies(copy.collectionId), (old) => {
    if (!old) return old;
    const others = old.copies.filter((c) => c.id !== copy.id);
    const sameRelease = [...others.filter((c) => c.releaseId === copy.releaseId), copy];
    const count = sameRelease.length;
    return {
      copies: [...others.map((c) => (c.releaseId === copy.releaseId ? { ...c, sameReleaseCount: count } : c)), { ...copy, sameReleaseCount: count }],
    };
  });
  void queryClient.invalidateQueries({ queryKey: keys.collections });
  void queryClient.invalidateQueries({ queryKey: keys.copy(copy.id) });
}

export function removeCopyFromCache(queryClient: QueryClient, collectionId: string, copyId: string) {
  queryClient.setQueryData<{ copies: CopyListItem[] }>(keys.copies(collectionId), (old) =>
    old ? { copies: old.copies.filter((c) => c.id !== copyId) } : old,
  );
  void queryClient.invalidateQueries({ queryKey: keys.collections });
}
